import { useState } from 'react';
import { getErrorMessage } from '../../lib/errors';
import { updateBrandIdentity } from './knowledgeBaseApi';
import { publishBrandIdentity, useBrandIdentity } from './useBrandIdentity';

/**
 * "Use brand memory in new posts": the brand's `brand_memory_enabled`.
 *
 * Writes a partial PUT carrying only this field, so nothing else on the
 * identity moves, and publishes the result so every open reader of the
 * identity sees it. The Brand identity form never sends this field (its draft
 * does not hold it), so a later save there cannot switch it back.
 */
export function BrandMemorySwitch({ businessId }: { businessId: string }) {
  const { identity, isLoading, error } = useBrandIdentity(businessId);
  /** The value being written, shown while the PUT is in flight. */
  const [pending, setPending] = useState<boolean | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // A server from before migration 0053 answers without the field; the
  // column defaults to on.
  const saved = identity?.brand_memory_enabled ?? true;
  const enabled = pending ?? saved;

  async function change(next: boolean) {
    setPending(next);
    setSaveError(null);
    try {
      const result = await updateBrandIdentity(businessId, { brand_memory_enabled: next });
      publishBrandIdentity(result);
    } catch (caught) {
      setSaveError(getErrorMessage(caught, 'That did not save. Try again.'));
    } finally {
      setPending(null);
    }
  }

  return (
    <div className="brand-memory__switch">
      <label className="field field--inline">
        <input
          type="checkbox"
          checked={enabled}
          disabled={isLoading || pending !== null}
          onChange={(event) => void change(event.target.checked)}
        />
        <span className="field__label">Use brand memory in new posts</span>
      </label>
      {!enabled ? (
        <p className="brand-memory__quiet">
          Brand memory is off. Autopostmate keeps learning but new posts will not use it.
        </p>
      ) : null}
      {error ? <p className="brand-memory__quiet">{error}</p> : null}
      {saveError ? (
        <p className="form__error" role="alert">
          {saveError}
        </p>
      ) : null}
    </div>
  );
}
